import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import {
  GetTimelapseVideoQuery,
  GetTimelapseVideoQueryVariables,
} from "../../../API";
import { GET_TIMELAPSE_VIDEO } from "../../../common/operations/queries";

export interface GetTimelapseVideoResponse {
  deviceIdClean: string;
  nodeIdClean: string;
  bucketName: string;
  prefix: string;
  date: string;
  files: string[];
  playlist: string | null;
  availableDates?: string[];
  message?: string;
}

export const useGetTimelapseVideo = (
  deviceIdClean: string,
  nodeIdClean: string,
  date: string,
  options?: {
    skip?: boolean;
    onCompleted?: (data: GetTimelapseVideoResponse) => void;
    onError?: (error: Error) => void;
  }
) => {
  const { data, loading, error, refetch } = useQuery<
    GetTimelapseVideoQuery,
    GetTimelapseVideoQueryVariables
  >(GET_TIMELAPSE_VIDEO, {
    variables: {
      input: {
        deviceIdClean,
        nodeIdClean,
        date,
      },
    },
    skip: !deviceIdClean || !nodeIdClean || !date || options?.skip,
    onError: options?.onError,
  });

  const parsedData = useMemo<GetTimelapseVideoResponse | null>(() => {
    if (!data?.getTimelapseVideo) return null;

    try {
      const parsed = JSON.parse(data.getTimelapseVideo as string);

      if (parsed.statusCode === 200) {
        return parsed.body as GetTimelapseVideoResponse;
      } else {
        console.error("Lambda returned error status:", parsed);

        return null;
      }
    } catch (err) {
      console.error("Error parsing getTimelapseVideo response:", err);

      return null;
    }
  }, [data?.getTimelapseVideo]);

  useEffect(() => {
    if (parsedData && options?.onCompleted) {
      options.onCompleted(parsedData);
    }
  }, [parsedData, options?.onCompleted]);

  return {
    data: parsedData,
    loading,
    error,
    refetch,
    files: parsedData?.files || [],
    playlist: parsedData?.playlist || null,
    message: parsedData?.message,
    availableDates: parsedData?.availableDates || [],
  };
};
